import './edit-report-screen.scss'

import _ from 'lodash'
import { EMPTY, forkJoin, of } from 'rxjs'
import { catchError, first, map, switchMap } from 'rxjs/operators'
import { faCaretDown, faCaretRight, faSave } from '@fortawesome/free-solid-svg-icons'
import { defineComponent, markRaw } from 'vue'

import {
    ArchiveResource, Alteration, ConditionReport, CounterSignatureResource, EntityField, EntityFieldStatus,
    SignerResource, Signer, Wemi, ConditionReportState, RecordMaskResource,
    createTranslatedString, convertSignerToCounterSigner, computeAlterationsIndexes,
    getTranslatedString, removeRefsInAssociatedMedias, CounterSignerResource, ConditionReportStatePrefix,
    SpecimenResource,
} from '@skinsoft/types'
import { ConditionReportRoutes } from '@skinsoft/const'
import { conditionReportSelectors, mediaSelectors } from '@skinsoft/selectors'
import { firstMedia, referenceNumber } from '@skinsoft/selectors/condition-report-selectors'
import { ConditionReportDataStore, MediaDataStore } from '@skinsoft/entity'
import { WemiUtils } from '@skinsoft/wemi-utils'
import { ConditionReportEditScreenHeader } from '@skinsoft/entity-ui'
import { ConditionReportPDFTransferHelper, ImageTransferHelper, MediaTransferHelper } from '@skinsoft/media-transfer-helper'
import { DATE_FORMATS, formatCurrentDate } from '@skinsoft/utils'

import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { DISPLAY_ALERT_TOPIC, SAVE_GENERIC_EDIT_SCREEN } from '@skinsoft/bus'
import { MEDIA_ID_CHANGED_FROM_CACHE, PRINCIPAL_IMAGE_CLICK_TOPIC, DELETE_IMAGE_CLICK_TOPIC, FREE_MEDIA_CACHE_TOPIC } from '@skinsoft/bus/src/busTopics'

import {
    CLOSE_CLOSE_REPORT_SCREEN_TOPIC, CLOSE_COUNTER_SIGNATURE_MODULE_TOPIC, CLOSE_DRAWING_EDITOR_TOPIC,
    CLOSE_PDF_OVERVIEW_TOPIC, CLOSE_SIGNATURE_MODULE_TOPIC, COUNTER_SIGNATURE_UPDATED,
    DELETE_CURRENT_REPORT_TOPIC, DELETE_UNSYNCHRONIZED_MODIFICATIONS_CR_TOPIC,
    DUPLICATE_CLOSED_REPORT_TOPIC, OPEN_CLOSE_REPORT_SCREEN_TOPIC, OPEN_COUNTER_SIGNATURE_MODULE_TOPIC,
    OPEN_DRAWING_EDITOR_TOPIC, OPEN_PDF_OVERVIEW_TOPIC, OPEN_SIGNATURE_MODULE_TOPIC,
    REMOVE_COUNTER_SIGNATURE, REPORT_EDITOR_CLOSE_ALL_TABS_TOPIC, REPORT_EDITOR_OPEN_ALL_TABS_TOPIC,
    SAVE_CONTRE_SIGNED_REPORT, SAVE_CURRENT_REPORT_TOPIC,
} from '../../../busTopics'
import { CloseReportScreen } from '../../component/close-report-screen'
import { SignatureModuleScreen } from '../../component/signature-module/signature-module-screen'
import { PDFOverviewScreen } from '../../pdf-overview-screen'
import { DrawingEditor } from '../drawing-editor-components/drawing-editor'
import { AddMediaEvent } from '@skinsoft/entity-ui/src/media/medias-fields-area'
import { Message } from '@skinsoft/bus'
import AlterationArea from '../alteration-area'
import { DeleteImageClickEvent } from '@skinsoft/entity-ui/src/media/medias-area'
import { MediaIdChangedFromCacheEvent } from '@skinsoft/ui/src/item-card/media'
import { ALTERATION_EDITED, AlterationChanged, DELETE_ALTERATION } from '../drawing-editor-components/alteration-line'
import { ALTERATION_CREATED } from '../alteration-area/create-alteration-form'
import { ALTERATION_CARD_ALTERATION_UPDATED, ALTERATION_CARD_DELETE_ALTERATION } from '../alteration-area/alteration-card'
import { CounterSignatureModuleScreen } from '../../component/signature-module/counter-signature-module-screen'
import { ConditionReportPDFGenerator } from '@skinsoft/pdf-generator/condition-report-pdf-generator'
import { MaskFieldUpdateEvent } from '@skinsoft/record-mask/src/components/mask-field'
import { RecordMaskDisplay } from '@skinsoft/record-mask'
import { closedReportSummaryAreaRecordMasks, identifierAndContextRecordMasks, summaryAreaRecordMasks } from '../../../local-record-masks'
import { GenericEditScreen, GenericEditScreenSection } from '@skinsoft/pages/generic/generic-edit-screen'
import { GENERIC_EDIT_SCREEN_MEDIA_ADDED } from '@skinsoft/events'
import {
    ConditionReportEditScreenController,
    ConditionReportEditScreenControllerCallbacks,
} from './condition-report-edit-screen-controller'

declare const DISABLED_FIELDS: { fieldName: string, message?: string, offlineOnly: boolean }[]

const REFERENCE_NUMBER_ENTITY_FIELD_ID = '5a3925ec08813b0001387c63'

const ALTERATION_SECTION_ID = 'alteration'

export const EditReportScreen = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sCloseReportScreen: CloseReportScreen,
        sDrawingEditor: DrawingEditor,
        sPdfOverviewScreen: PDFOverviewScreen,
        sSignatureModuleScreen: SignatureModuleScreen,
        sCounterSignatureModuleScreen: CounterSignatureModuleScreen,
        sGenericEditScreen: GenericEditScreen,
        sConditionReportEditScreenHeader: ConditionReportEditScreenHeader,
    },
    props: {
        readOnly: {
            type: Boolean,
            default: false,
        },
        navigateBackRoute: {
            type: String,
        },
    },
    template: require('./edit-report-screen.html'),
    data() {
        return {
            // Entity & associated data
            conditionReport: null as any as ConditionReport,
            associatedEntity: null as Wemi | ArchiveResource | SpecimenResource | null,
            mediasWithoutPDF: [] as String[],
            pdfMedias: [] as String[],

            // Drawing editor
            drawingEditorParameters: {} as any,

            // Icons
            caretDownIcon: faCaretDown,
            caretRightIcon: faCaretRight,
            saveIcon: faSave,

            // Modal visibility
            showDrawingEditor: false,
            showPdfOverview: false,
            showSignatureModule: false,
            showCounterSignatureModule: false,
            showCloseReportScreen: false,

            selectedCounterSignature: null as CounterSignatureResource | null,

            hasItem: true,

            // Controller & state
            viewController: null as ConditionReportEditScreenController | null,
            fieldsErrors: [] as { entityId: string, message: string }[],
            disabledFields: [] as { fieldName: string, message?: string, offlineOnly: boolean }[],

            deleteErrorMessage: '',
            deleteSuccessMessage: '',

            isOnline: window.navigator.onLine,

            lastSignerId: 1,
            lastCounterSignatureId: 1,

            SAVE_CONTRE_SIGNED_REPORT: SAVE_CONTRE_SIGNED_REPORT,
            REMOVE_COUNTER_SIGNATURE: REMOVE_COUNTER_SIGNATURE,
            DELETE_IMAGE_CLICK_TOPIC: DELETE_IMAGE_CLICK_TOPIC,
            MEDIA_ID_CHANGED_FROM_CACHE: MEDIA_ID_CHANGED_FROM_CACHE,
            PRINCIPAL_IMAGE_CLICK_TOPIC: PRINCIPAL_IMAGE_CLICK_TOPIC,
            ALTERATION_CREATED: ALTERATION_CREATED,
            DELETE_ALTERATION: DELETE_ALTERATION,
            ALTERATION_CARD_ALTERATION_UPDATED: ALTERATION_CARD_ALTERATION_UPDATED,
            GENERIC_EDIT_SCREEN_MEDIA_ADDED: GENERIC_EDIT_SCREEN_MEDIA_ADDED,
        }
    },
    computed: {
        isReady(): boolean {
            return this.viewController !== null
        },

        contentHidden(): boolean {
            return this.showDrawingEditor ||
                this.showPdfOverview ||
                this.showSignatureModule ||
                this.showCloseReportScreen ||
                this.showCounterSignatureModule
        },

        additionnalEditReportScreenClasses(): { 'drawing-editor': boolean } {
            return {
                'drawing-editor': this.showDrawingEditor,
            }
        },

        recordMasks(): RecordMaskResource[] {
            return [
                identifierAndContextRecordMasks,
                this.readOnly ? closedReportSummaryAreaRecordMasks : summaryAreaRecordMasks,
            ]
        },

        additionalSections(): GenericEditScreenSection[] {
            return [
                {
                    id: ALTERATION_SECTION_ID,
                    // markRaw: without it, this feeds GenericEditScreen's reactive `sections` array
                    // and gets deep-proxied — no benefit, and exactly the overhead Vue warns about.
                    component: markRaw(AlterationArea),
                    sectionTitle: this.$t('editReportScreen.alterations.tabTitle') as string,
                    props: () => ({
                        associatedMedias: this.mediasWithoutPDF ?? [],
                        alterations: this.conditionReport?.conditionReportCS?.alterations ?? [],
                        readOnly: this.readOnly,
                        existingTitles: this.alterationsTitles,
                    }),
                    eventListeners: () => ({
                        addMedia: this.onAddMedia,
                        [ALTERATION_EDITED]: this.onAlterationChanged,
                        [DELETE_IMAGE_CLICK_TOPIC]: this.onDeleteMedia,
                        [MEDIA_ID_CHANGED_FROM_CACHE]: this.onMediaIdChangedFromCache,
                        [PRINCIPAL_IMAGE_CLICK_TOPIC]: this.onChangeFirstMedia,
                        [ALTERATION_CREATED]: this.addAlteration,
                        [DELETE_ALTERATION]: this.onDeleteAlteration,
                        [ALTERATION_CARD_ALTERATION_UPDATED]: this.onAlterationChanged,
                        [ALTERATION_CARD_DELETE_ALTERATION]: this.onDeleteAlteration,
                    }),
                    dataCy: 'edit-report-screen--alteration-section',
                },
            ]
        },

        reportTitle(): string {
            return this.$t('editReportScreen.reportTitlePrefix', {
                refNumber: conditionReportSelectors.referenceNumber(this.conditionReport),
            }) as string
        },

        reportStatus(): string | undefined {
            const status = conditionReportSelectors.workflowState(this.conditionReport)
            if(status) {
                return `state.${status.replace(ConditionReportStatePrefix, '')}`
            }
            return undefined
        },

        alterationsTitles(): string[] {
            if (this.conditionReport?.conditionReportCS?.alterations) {
                return _.uniq(
                    this.conditionReport.conditionReportCS.alterations
                        .filter(alteration => {
                            if (!alteration.title?.translatedString) return false
                            if (!alteration.title.translatedString[this.getAppLocale()]) return false
                            return true
                        })
                        .map(alteration => (getTranslatedString(alteration.title, this.getAppLocale()) ?? '') as string)
                ).sort((a, b) => a.localeCompare(b))
            }
            return []
        },

        signers(): SignerResource[] | undefined {
            if (!this.conditionReport?.conditionReportCS?.signers) {
                _.set(this.conditionReport, 'conditionReportCS.signers', [])
            }
            return this.conditionReport.conditionReportCS!.signers
        },

        counterSignatures(): CounterSignatureResource[] | undefined {
            if (!this.conditionReport?.conditionReportCS?.counterSignatures) {
                _.set(this.conditionReport, 'conditionReportCS.counterSignatures', [])
            }
            return this.conditionReport.conditionReportCS!.counterSignatures
        },
    },
    mounted() {
        this.viewController = new ConditionReportEditScreenController({
            onEntityFetched: (entity) => {
                this.conditionReport = entity
                this.fetchAssociatedEntity(entity)
                this.filterPDFMedias()
            },
            on409Error: () => {
                this.fieldsErrors = [{
                    entityId: REFERENCE_NUMBER_ENTITY_FIELD_ID,
                    message: this.$t('editItemScreen.errorMessages.inventoryNumberNotUnique') as string,
                }]
                this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                    message: this.$t('editReportScreen.saveAlert.inventoryNumberNotUnique'),
                    variant: 'danger',
                })
            },
        })

        this.deleteErrorMessage = this.$t('editReportScreen.deleteAlert.errorMessage') as string
        this.deleteSuccessMessage = this.$t('editReportScreen.deleteAlert.successMessage') as string

        this.disabledFields = DISABLED_FIELDS.map(disabledField => {
            if (disabledField.message) {
                disabledField.message = this.$t(disabledField.message) as string
            }
            return disabledField
        })

        window.addEventListener('online', this.updateConnexionStatus)
        window.addEventListener('offline', this.updateConnexionStatus)

        this.subscribeToEvents()
    },
    beforeUnmount() {
        window.removeEventListener('online', this.updateConnexionStatus)
        window.removeEventListener('offline', this.updateConnexionStatus)
    },
    methods: {
        updateConnexionStatus() {
            this.isOnline = window.navigator.onLine
        },

        subscribeToEvents() {
            this.subscribeToBus(SAVE_CURRENT_REPORT_TOPIC, () => {
                this.fieldsErrors = []
                this.emitOnBus(SAVE_GENERIC_EDIT_SCREEN)
            }, 'save-report-subscription')

            this.subscribeToBus(DELETE_CURRENT_REPORT_TOPIC, () => {
                this.deleteCurrentReport()
            }, 'delete-report-subscription')

            this.subscribeToBus(DELETE_UNSYNCHRONIZED_MODIFICATIONS_CR_TOPIC, () => {
                this.deleteModificationsNotSynchronized()
            })

            this.subscribeToBus(OPEN_DRAWING_EDITOR_TOPIC, this.onOpenDrawingEditor)
            this.subscribeToBus(CLOSE_DRAWING_EDITOR_TOPIC, this.onDrawingEditorClosed)

            this.subscribeToBus(OPEN_PDF_OVERVIEW_TOPIC, () => {
                this.showDrawingEditor = false
                this.showCloseReportScreen = false
                this.showSignatureModule = false
                this.showPdfOverview = true
                this.showCounterSignatureModule = false
            })

            this.subscribeToBus(CLOSE_PDF_OVERVIEW_TOPIC, () => {
                this.showPdfOverview = false
            })

            this.subscribeToBus(OPEN_SIGNATURE_MODULE_TOPIC, () => {
                this.showDrawingEditor = false
                this.showPdfOverview = false
                this.showCloseReportScreen = false
                this.showSignatureModule = true
            })

            this.subscribeToBus(CLOSE_SIGNATURE_MODULE_TOPIC, () => {
                this.showSignatureModule = false
            })

            this.subscribeToBus(OPEN_COUNTER_SIGNATURE_MODULE_TOPIC, () => {
                this.showDrawingEditor = false
                this.showPdfOverview = false
                this.showCloseReportScreen = false
                this.showSignatureModule = false
                this.onOpenCounterSignatureModule()
                this.showCounterSignatureModule = true
            })

            this.subscribeToBus(CLOSE_COUNTER_SIGNATURE_MODULE_TOPIC, () => {
                this.showCounterSignatureModule = false
            })

            this.subscribeToBus(OPEN_CLOSE_REPORT_SCREEN_TOPIC, async () => {
                this.emitOnBus(SAVE_GENERIC_EDIT_SCREEN)
                this.showDrawingEditor = false
                this.showCloseReportScreen = true
                this.showSignatureModule = false
                this.showCounterSignatureModule = false
            })

            this.subscribeToBus(CLOSE_CLOSE_REPORT_SCREEN_TOPIC, () => {
                this.showCloseReportScreen = false
            })

            this.subscribeToBus(DUPLICATE_CLOSED_REPORT_TOPIC, () => {
                this.duplicateReportIfClosed()
            })
        },

        onCollapseAllClick() {
            this.emitOnBus(REPORT_EDITOR_CLOSE_ALL_TABS_TOPIC)
        },

        onDeployAllClick() {
            this.emitOnBus(REPORT_EDITOR_OPEN_ALL_TABS_TOPIC)
        },

        filterPDFMedias() {
            if(this.conditionReport?.associatedMedias?.medias) {
                if(navigator.onLine) {
                    const PAGE_SIZE = 100
                    const ids = this.conditionReport.associatedMedias.medias

                    MediaDataStore.getInstance().searchAll({ filteredIds: ids, pageSize: PAGE_SIZE, page: 0 }).pipe(
                        first(),
                        switchMap(firstPage => {
                            const totalPages = Math.ceil(firstPage.totalSize / PAGE_SIZE)
                            if(totalPages <= 1) {
                                return of(firstPage.allData ?? [])
                            }
                            const remainingPages$ = Array.from({ length: totalPages - 1 }, (_, i) =>
                                MediaDataStore.getInstance().searchAll({ filteredIds: ids, pageSize: PAGE_SIZE, page: i + 1 }).pipe(first())
                            )
                            return forkJoin(remainingPages$).pipe(
                                map(pages => [
                                    ...(firstPage.allData ?? []),
                                    ...pages.flatMap(p => p.allData ?? [])
                                ])
                            )
                        })
                    ).subscribe(allMedias => {
                        const PDF_REGEX = /\.pdf$/

                        const [mediasWithoutPDF, pdfMedias] = _.partition(
                            allMedias,
                            media => {
                                const fileName = mediaSelectors.fileName(media)
                                if(typeof fileName === "string" || fileName instanceof String) {
                                    if(fileName.trim().match(PDF_REGEX)) {
                                        return false
                                    }
                                }
                                return true
                            }
                        )

                        const mediasIdsWithoutPDF = mediasWithoutPDF.map(media => mediaSelectors.id(media))

                        // we make sure that mediasWithoutPDF preserve associated medias order
                        this.mediasWithoutPDF = ids.filter(mediaId => mediasIdsWithoutPDF.includes(mediaId))
                        this.pdfMedias = pdfMedias.map(media => mediaSelectors.id(media))
                    })
                } else {
                    this.mediasWithoutPDF = _.cloneDeep(conditionReportSelectors.medias(this.conditionReport))
                    this.pdfMedias = []
                }
            }
        },

        onFieldUpdated(event: MaskFieldUpdateEvent) {
            if (this.conditionReport && this.viewController) {
                this.viewController.updateEntity(event, this.conditionReport)
            }
        },

        onAddMedia(image: AddMediaEvent) {
            const medias = this.conditionReport.associatedMedias.medias ?? []

            if (image.newMediaId) {
                if (medias === null) {
                    this.conditionReport.associatedMedias.medias = [image.newMediaId]
                    this.mediasWithoutPDF = [image.newMediaId]
                } else if (!medias.includes(image.newMediaId)) {
                    this.conditionReport.associatedMedias.medias = medias.filter(id => id !== image.cacheKey)
                    this.conditionReport.associatedMedias.medias.push(image.newMediaId)

                    this.mediasWithoutPDF = this.mediasWithoutPDF.filter(id => id !== image.cacheKey)
                    this.mediasWithoutPDF.push(image.newMediaId)
                }
            } else {
                if (medias === null) {
                    this.conditionReport.associatedMedias.medias = [image.cacheKey]
                    this.mediasWithoutPDF = [image.cacheKey]
                } else if (!medias.includes(image.cacheKey)) {
                    if (this.conditionReport.associatedMedias?.medias) {
                        this.conditionReport.associatedMedias.medias.push(image.cacheKey)
                    } else {
                        _.set(this.conditionReport, 'associatedMedias.medias', [image.cacheKey])
                    }
                    this.mediasWithoutPDF.push(image.cacheKey)
                }
            }
        },

        onGenericEditScreenMediaAdded(image: AddMediaEvent) {
            const mediaId = image.newMediaId ?? image.cacheKey
            if (mediaId && !this.mediasWithoutPDF.includes(mediaId)) {
                this.mediasWithoutPDF.push(mediaId)
            }
        },

        onDeleteMedia(event: DeleteImageClickEvent) {
            const updatedMedias = this.conditionReport.associatedMedias?.medias?.filter(mediaId => {
                return mediaId !== event.imageId
            })
            _.set(this.conditionReport, 'associatedMedias.medias', updatedMedias)

            this.mediasWithoutPDF = this.mediasWithoutPDF.filter(mediaId => mediaId !== event.imageId)

            const principalMedia = firstMedia(this.conditionReport)
            if (principalMedia === event.imageId) {
                _.set(this.conditionReport, 'associatedMedias.first', null)
            }
        },

        onChangeFirstMedia(mediaId: string) {
            const mediasList: string[] = _.get(this.conditionReport, 'associatedMedias.medias', [])
            if (mediasList && mediasList.includes(mediaId)) {
                _.set(this.conditionReport, 'associatedMedias.first', mediaId)
            }
        },

        onMediaIdChangedFromCache(event: MediaIdChangedFromCacheEvent) {
            let index = this.conditionReport.associatedMedias?.medias?.indexOf(event.oldMediaId)
            if (index !== undefined && index !== -1) {
                this.conditionReport.associatedMedias!.medias!.splice(index, 1, event.newMediaId)

                const principalMedia = firstMedia(this.conditionReport)
                if (principalMedia === event.oldMediaId) {
                    _.set(this.conditionReport, 'associatedMedias.first', event.newMediaId)
                }

                index = this.mediasWithoutPDF.indexOf(event.oldMediaId)
                if (index !== -1) {
                    this.mediasWithoutPDF[index] = event.newMediaId
                }
            }
        },

        onAlterationChanged(event: AlterationChanged) {
            const oldValue = this.conditionReport.conditionReportCS?.alterations?.[event.alterationNumber]
            if (oldValue) {
                this.conditionReport.conditionReportCS!.alterations![event.alterationNumber] = _.merge(oldValue, event.updates)
            } else {
                _.set(this.conditionReport, 'conditionReportCS.alterations', [event.updates])
            }
            this.reNumberAlterations()
        },

        onDeleteAlteration(index: number) {
            this.conditionReport.conditionReportCS?.alterations?.splice(index, 1)
            this.reNumberAlterations()
        },

        addAlteration(alteration: Alteration) {
            if (this.conditionReport.conditionReportCS?.alterations) {
                this.conditionReport.conditionReportCS.alterations.push(alteration)
            } else {
                _.set(this.conditionReport, 'conditionReportCS.alterations', [alteration])
            }
            this.reNumberAlterations()
        },

        reNumberAlterations() {
            if (this.conditionReport?.conditionReportCS?.alterations?.length) {
                const renumberedAlterations = computeAlterationsIndexes(this.conditionReport)
                this.conditionReport.conditionReportCS.alterations = renumberedAlterations
            }
        },

        onAddSigner() {
            const blankSigner: Signer = {
                id: this.lastSignerId.toString(),
                signer: createTranslatedString(this.getAppLocale(), ''),
                signature: undefined,
                positionOfSignatory: undefined,
                dateOfSignature: undefined,
            }
            this.lastSignerId++

            if (this.conditionReport?.conditionReportCS?.signers) {
                this.conditionReport.conditionReportCS.signers.push(blankSigner)
            } else {
                _.set(this.conditionReport, 'conditionReportCS.signers', [blankSigner])
            }
        },

        onRemoveSigner(index: number) {
            this.conditionReport?.conditionReportCS?.signers?.splice(index, 1)
        },

        onSignerUpdated([updatedSigner, index]: [Signer, number]) {
            if (this.conditionReport?.conditionReportCS?.signers) {
                this.conditionReport.conditionReportCS.signers[index] = updatedSigner
            }
        },

        onCounterSignatureUpdated([updatedSignature, index]: [CounterSignatureResource, number]) {
            if (this.conditionReport?.conditionReportCS?.counterSignatures) {
                const indexInArray = this.conditionReport.conditionReportCS.counterSignatures.findIndex(s => s.index === index)
                if (indexInArray > -1) {
                    this.conditionReport.conditionReportCS.counterSignatures.splice(indexInArray, 1, updatedSignature)
                }
            }
        },

        onRemoveCounterSignature(index: number) {
            if (this.conditionReport?.conditionReportCS?.counterSignatures?.length) {
                const indexInArray = this.conditionReport.conditionReportCS.counterSignatures.findIndex(s => s.index === index)
                if (indexInArray > -1) {
                    this.conditionReport.conditionReportCS.counterSignatures.splice(indexInArray, 1)
                }
            }
        },

        onAddCounterSignature() {
            const blankCounterSignature: CounterSignatureResource = {
                index: this.lastCounterSignatureId,
                comment: createTranslatedString(this.getAppLocale(), ''),
                location: createTranslatedString(this.getAppLocale(), ''),
                reason: undefined,
                date: undefined,
                observationCarriedPresenceOfs: [],
            }
            this.lastCounterSignatureId++

            if (this.conditionReport?.conditionReportCS?.counterSignatures) {
                this.conditionReport.conditionReportCS.counterSignatures.push(blankCounterSignature)
            } else {
                _.set(this.conditionReport, 'conditionReportCS.counterSignatures', [blankCounterSignature])
            }
        },

        addCounterSignatureWithOriginalSigners() {
            const originalSigners: SignerResource[] = this.conditionReport?.conditionReportCS?.signers ?? []

            const defaultCounterSigners: CounterSignerResource[] = originalSigners.map((signer, index) => {
                let counterSigner = convertSignerToCounterSigner(signer)
                counterSigner.index = index + 1
                return counterSigner
            })

            const blankCounterSignature: CounterSignatureResource = {
                index: this.lastCounterSignatureId,
                comment: createTranslatedString(this.getAppLocale(), ''),
                location: createTranslatedString(this.getAppLocale(), ''),
                reason: undefined,
                date: undefined,
                observationCarriedPresenceOfs: defaultCounterSigners,
            }
            this.lastCounterSignatureId++

            if (this.conditionReport?.conditionReportCS?.counterSignatures) {
                this.conditionReport.conditionReportCS.counterSignatures.push(blankCounterSignature)
            } else {
                _.set(this.conditionReport, 'conditionReportCS.counterSignatures', [blankCounterSignature])
            }
        },

        onOpenCounterSignatureModule() {
            this.addCounterSignatureWithOriginalSigners()
            this.selectedCounterSignature = this.conditionReport.conditionReportCS?.counterSignatures?.find(cs => {
                return cs.index === this.lastCounterSignatureId - 1
            }) ?? null
        },

        async saveReportAfterCounterSignature(updateSignatureEvent: [CounterSignatureResource, number]) {
            this.onCounterSignatureUpdated(updateSignatureEvent)
            this.emitOnBus(SAVE_GENERIC_EDIT_SCREEN)

            if (window.navigator.onLine) {
                if (this.associatedEntity) {
                    this.emitOnBus(FREE_MEDIA_CACHE_TOPIC)
                    await new Promise<void>(r => setTimeout(r, 0))
                    ConditionReportPDFGenerator.getInstance().generatePDF(
                        this.conditionReport,
                        this.associatedEntity,
                        async (blob) => {
                            const datePrefix = formatCurrentDate(DATE_FORMATS.DD_MM_YYYY_DASH_HMS_ONE_BLOCK)
                            const inventoryNumber = referenceNumber(this.conditionReport)
                            const fileName = datePrefix ?
                                `${inventoryNumber}-${datePrefix}.pdf`
                                : `${datePrefix}.pdf`
                            const withName = new File([blob], fileName)

                            this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                                message: this.$t('editReportScreen.pdf.counterSignaturePDF.startGenerating'),
                                variant: 'info'
                            })
                            ConditionReportPDFTransferHelper.getInstance().uploadPDFDocument('application/pdf', this.conditionReport.id, withName, true)
                                .subscribe(async (mediaId: string) => {
                                    if (mediaId) {
                                        try {
                                            await ConditionReportDataStore.getInstance().addPDFToReport(
                                                conditionReportSelectors.id(this.conditionReport),
                                                mediaId,
                                            ).toPromise()
                                            this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                                                message: this.$t('editReportScreen.pdf.counterSignaturePDF.success'),
                                                variant: 'success'
                                            })
                                        } catch (err) {
                                            this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                                                message: this.$t('editReportScreen.pdf.counterSignaturePDF.failure'),
                                                variant: 'warning'
                                            })
                                        }
                                    } else {
                                        this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                                            message: this.$t('editReportScreen.pdf.counterSignaturePDF.failure'),
                                            variant: 'warning'
                                        })
                                    }
                                })
                        },
                        this.$i18n.locale,
                        this.pdfMedias as string[],
                        false
                    ).catch(() => {
                        this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                            message: this.$t('pdfDocument.generationFailureAlert'),
                            variant: 'danger',
                        })
                    })
                } else {
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                        message: this.$t('pdfDocument.generationFailureAlert'),
                        variant: 'danger',
                    })
                }
            }
        },

        openDrawingEditor(mediaId?: string, dataUrl?: string) {
            this.reNumberAlterations()

            this.drawingEditorParameters = {
                mediaId: mediaId,
                mediaUrl: dataUrl,
                mediaAlterations: this.conditionReport.conditionReportCS?.alterations?.filter(
                    alteration => alteration.media === mediaId
                ) ?? [],
            }
            this.drawingEditorParameters.firstAlterationIndex = this.drawingEditorParameters.mediaAlterations?.[0]?.index
            this.showPdfOverview = false
            this.showDrawingEditor = true
        },

        async onOpenDrawingEditor(message: Message) {
            if (message.payload) {
                const mediaId = message.payload

                if (MediaTransferHelper.isLocalMedia(mediaId)) {
                    const imageTransferHelper = (await import('@skinsoft/media-transfer-helper')).ImageTransferHelper.getInstance()
                    imageTransferHelper.downloadImageOffline(mediaId).subscribe(res => {
                        if (res.dataUrl) {
                            this.openDrawingEditor(mediaId, res.dataUrl)
                        }
                    })
                } else {
                    const medias = this.conditionReport.associatedMedias.medias
                    if (medias && medias.includes(mediaId)) {
                        this.openDrawingEditor(mediaId)
                    }
                }
            }
        },

        async onDrawingEditorClosed(message: Message) {
            if (message.payload) {
                const updatedAlterations: Alteration[] = message.payload.newData
                const mediaId = message.payload.alterationMediaId ?? updatedAlterations?.[0]?.media

                if (message.payload.saveReport && mediaId) {
                    let upToDateMediaId = mediaId

                    if (MediaTransferHelper.isLocalMedia(mediaId)) {
                        const newMediaId = MediaTransferHelper.getInstance().getMediaIdFromCacheKey(mediaId)
                        if (newMediaId) {
                            upToDateMediaId = newMediaId
                            for (const updatedAlteration of updatedAlterations) {
                                if (updatedAlteration.media === mediaId) {
                                    updatedAlteration.media = upToDateMediaId
                                }
                            }
                        }
                    }

                    if (updatedAlterations && updatedAlterations.length) {
                        let alterationsWithoutUpdatedOnes = this.conditionReport.conditionReportCS?.alterations?.filter(
                            alteration => alteration.media !== mediaId && alteration.media !== upToDateMediaId
                        ) ?? []

                        _.set(
                            this.conditionReport,
                            'conditionReportCS.alterations',
                            alterationsWithoutUpdatedOnes.concat(updatedAlterations)
                        )
                    } else {
                        if (this.conditionReport.conditionReportCS?.alterations?.length) {
                            this.conditionReport.conditionReportCS.alterations = this.conditionReport.conditionReportCS.alterations.filter(
                                alteration => alteration.media !== mediaId
                            )
                        }
                    }

                    this.showDrawingEditor = false
                    this.emitOnBus(SAVE_GENERIC_EDIT_SCREEN)
                    return
                }
            }
            this.showDrawingEditor = false
        },

        deleteCurrentReport() {
            ConditionReportDataStore.getInstance().delete(this.conditionReport.id).pipe(first()).toPromise()
                .then(() => {
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                        message: this.deleteSuccessMessage,
                        variant: 'success',
                    })
                })
                .catch(() => {
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                        message: this.deleteErrorMessage,
                        variant: 'danger',
                    })
                })
        },

        deleteModificationsNotSynchronized() {
            if (this.conditionReport.associatedMedias?.medias) {
                for (const mediaId of this.conditionReport.associatedMedias.medias) {
                    if (MediaTransferHelper.isLocalMedia(mediaId)) {
                        const { ImageTransferHelper } = require('@skinsoft/media-transfer-helper')
                        ImageTransferHelper.getInstance().deleteImageInCache(mediaId)
                    }
                }
            }
            ConditionReportDataStore.getInstance().removeObjectFromUpdateCache(this.conditionReport.id)
                .then(() => {
                    (this.$refs.genericEditScreen as any)?.fetchEntity()
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                        message: this.$t('editReportScreen.deleteModificationsNotification.successMessage'),
                        variant: 'success',
                    })
                })
                .catch(() => {
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                        message: this.$t('editReportScreen.deleteModificationsNotification.errorMessage'),
                        variant: 'danger',
                    })
                })
        },

        async duplicateReportIfClosed() {
            if (conditionReportSelectors.status(this.conditionReport) === ConditionReportState.CLOSED) {
                let body: ConditionReport = this.conditionReport

                const pdfIds = _.get(this.conditionReport, 'associatedMedias.medias', [])
                    .filter(mediaId => !this.mediasWithoutPDF.includes(mediaId))

                if (pdfIds) {
                    body = _.cloneDeep(this.conditionReport)
                    const associatedMediasWithoutPDF = removeRefsInAssociatedMedias(body.associatedMedias, pdfIds)
                    _.set(body, 'associatedMedias', associatedMediasWithoutPDF)
                }

                ConditionReportDataStore.getInstance().duplicate(this.conditionReport.id, undefined, body).pipe(
                    catchError(error => {
                        this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                            message: this.$t('editReportScreen.alerts.duplicateClosedReport.failure'),
                            variant: 'danger',
                        })
                        return EMPTY
                    }),
                ).subscribe(createdReport => {
                    this.$router.push({
                        name: ConditionReportRoutes.EDIT_REPORT,
                        params: { reportId: createdReport.id },
                    })
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                        message: this.$t('editReportScreen.alerts.duplicateClosedReport.success'),
                        variant: 'success',
                    })
                })
            } else {
                this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                    message: this.$t('editReportScreen.alerts.duplicateClosedReport.warningReportNotClosed'),
                    variant: 'danger',
                })
            }
        },

        fetchAssociatedEntity(conditionReport: ConditionReport) {
            const linkableItem = conditionReportSelectors.linkableItem(conditionReport)
            if (linkableItem && linkableItem.linkableItem) {
                if (linkableItem.linkableItemReferenceType) {
                    switch (linkableItem.linkableItemReferenceType.toLowerCase()) {
                        case 'archive':
                            this.$archiveDataStore.fetchOne(linkableItem.linkableItem).subscribe(
                                archive => { this.associatedEntity = archive },
                                () => { this.associatedEntity = null; this.hasItem = false },
                            )
                        case 'specimen':
                            this.$specimenDataStore.fetchOne(linkableItem.linkableItem).subscribe(
                                specimen => { this.associatedEntity = specimen },
                                () => { this.associatedEntity = null; this.hasItem = false },
                            )
                        default: 
                            WemiUtils.getWemiFromItem(linkableItem.linkableItem).subscribe(
                                wemi => { this.associatedEntity = wemi },
                                () => { this.associatedEntity = null; this.hasItem = false },
                            )
                    }
                } else {
                    WemiUtils.getWemiFromItem(linkableItem.linkableItem).subscribe(
                        wemi => { this.associatedEntity = wemi },
                        () => { this.associatedEntity = null; this.hasItem = false },
                    )
                }
            } else {
                this.hasItem = false
            }
        },

        getFieldStatus(fieldName: string) {
            const disabledField = this.disabledFields?.find(f => f.fieldName === fieldName)
            if (disabledField) {
                if (!disabledField.offlineOnly || !this.isOnline) {
                    return {
                        disabled: true,
                        disabledMessage: disabledField.message,
                    }
                }
            }
            return { disabled: false }
        },

        getEntityFieldStatus(entityField: EntityField): EntityFieldStatus {
            const fieldStatus = this.getFieldStatus(`${entityField.entity}.${entityField.path}`)

            const errorField = this.fieldsErrors?.find(error => {
                return error.entityId === (entityField as any).entityFieldId
            })

            return {
                ...fieldStatus,
                error: !!errorField,
                errorMessage: errorField?.message,
            } as EntityFieldStatus
        },
    },
})
